import React from 'react';

/**
 * Sets or restores the header's z-index when a modal is open/closed
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {string} headerSelector - CSS selector for the header element (default: 'header, .MuiAppBar-root, .RaAppBar-root')
 */
export const setHeaderZIndex = (
  isOpen: boolean,
  headerSelector: string = 'header, .MuiAppBar-root, .RaAppBar-root'
) => {
  const header = document.querySelector(headerSelector) as HTMLElement;
  if (!header) return;

  if (isOpen) {
    // Save original z-index if not already saved
    if (!header.dataset.originalZIndex) {
      header.dataset.originalZIndex = window.getComputedStyle(header).zIndex || '';
    }
    header.style.zIndex = '0';
  } else {
    // Restore original z-index
    const originalZIndex = header.dataset.originalZIndex;
    if (originalZIndex !== '') {
      header.style.zIndex = originalZIndex;
    } else {
      header.style.zIndex = '';
    }
    delete header.dataset.originalZIndex;
  }
};

/**
 * Saves the current scroll position
 *
 * @returns {{x: number, y: number}} Current scroll position
 */
export const saveScrollPosition = (): { x: number; y: number } => ({
  x: window.scrollX,
  y: window.scrollY,
});

/**
 * Sets the window scroll position
 *
 * @param {{x: number, y: number}} position - Scroll position to set
 */
export const setScrollPosition = ({ x, y }: { x: number; y: number }) => {
  window.scroll(x, y);
};

/**
 * Sets or restores body overflow when a modal is open/closed
 *
 * @param {boolean} isOpen - Whether the modal is open
 */
export const setBodyOverflow = (isOpen: boolean) => {
  document.body.style.overflow = isOpen ? 'hidden' : 'visible';
};

/**
 * Hook to handle modal-related side effects (scroll, body overflow, header z-index)
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {{x: number, y: number}} savedPosition - Saved scroll position to restore when modal closes
 * @param {string} headerSelector - CSS selector for the header element
 */
export const useModalEffects = (
  isOpen: boolean,
  savedPosition: { x: number; y: number },
  headerSelector: string = 'header, .MuiAppBar-root, .RaAppBar-root'
) => {
  React.useEffect(() => {
    // Set scroll position
    const windowLocation = {
      x: isOpen ? 0 : savedPosition.x,
      y: isOpen ? 0 : savedPosition.y,
    };
    setScrollPosition(windowLocation);

    // Set body overflow
    setBodyOverflow(isOpen);

    // Set header z-index
    setHeaderZIndex(isOpen, headerSelector);
  }, [isOpen, savedPosition, headerSelector]);
};
